import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
  type ChartOptions
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useMemo, useState } from "react";
import { chartValueLabelsPlugin, withValueLabels } from "../../components/chartValueLabels";
import { PermissionGate } from "../../components/PermissionGate";
import {
  EMPTY_ENGAGEMENT_FILTERS,
  MIN_AGGREGATE_SIZE,
  STATUS_COLORS,
  buildEngagementOptions,
  buildEngagementSummary,
  filterEngagementRows,
  formatNumber,
  formatPercent,
  snapshotRows
} from "./engajamentoModel";
import {
  EngagementEmpty,
  EngagementError,
  EngagementFilterBar,
  EngagementHeader,
  EngagementLoading,
  EngagementPanel,
  EngagementPrivacyEmpty
} from "./EngagementUi";
import { useEngagementSnapshot } from "./useEngagementSnapshot";
import type { BreakdownItem, EngagementFilters } from "./types";
import "./engajamento.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, chartValueLabelsPlugin);

const BAR_COLORS = ["#1d446c", "#c8102e", "#0f8a69", "#d99a1b", "#6d3fc0", "#39769f", "#c45f16", "#64748b", "#94a3b8"];

export function AnaliseGraficaPage() {
  const { snapshot, loading, error, retry } = useEngagementSnapshot();
  const [filters, setFilters] = useState<EngagementFilters>({ ...EMPTY_ENGAGEMENT_FILTERS });
  const options = useMemo(() => snapshot ? buildEngagementOptions(snapshot) : emptyOptions(), [snapshot]);
  const rows = useMemo(() => snapshot ? filterEngagementRows(snapshotRows(snapshot), filters) : [], [filters, snapshot]);
  const summary = useMemo(() => buildEngagementSummary(rows, snapshot?.data_geracao), [rows, snapshot?.data_geracao]);

  function updateFilter(key: keyof EngagementFilters, value: string) {
    setFilters((current) => ({ ...current, [key]: value }));
  }

  return (
    <PermissionGate permission="graficos">
      <div className="engagement-page">
        {loading ? <EngagementLoading /> : null}
        {error ? <EngagementError message={error} onRetry={retry} /> : null}
        {snapshot && !loading ? (
          <>
            <EngagementHeader
              eyebrow="Análise visual"
              title="Análise gráfica"
              description="Distribuições agregadas de situação, estrutura acadêmica e comportamento de acesso."
              generatedAt={snapshot.data_geracao}
            />
            <EngagementFilterBar
              filters={filters}
              options={options}
              total={summary.total}
              onChange={updateFilter}
              onReset={() => setFilters({ ...EMPTY_ENGAGEMENT_FILTERS })}
            />

            {!summary.total ? <EngagementEmpty /> : summary.total < MIN_AGGREGATE_SIZE ? <EngagementPrivacyEmpty /> : (
              <section className="engagement-chart-grid" aria-label="Gráficos de engajamento">
                <EngagementPanel title="Situação de engajamento" subtitle="Quantidade e participação no recorte" help="Cada barra conta os estudantes na situação indicada. O percentual divide essa quantidade pelo total de estudantes do recorte atual.">
                  <RankedBarChart
                    items={summary.status}
                    ariaLabel="Distribuição dos estudantes por situação de engajamento"
                    colors={summary.status.map((item) => STATUS_COLORS[item.label as keyof typeof STATUS_COLORS] || STATUS_COLORS.Outros)}
                  />
                </EngagementPanel>
                <EngagementPanel title="Unidades acadêmicas" subtitle="Distribuição da base filtrada por UA" help="Cada barra conta estudantes ligados à unidade acadêmica. O percentual usa o total de estudantes do recorte; grupos com menos de cinco são reunidos em Demais grupos.">
                  <RankedBarChart items={summary.byUa} ariaLabel="Estudantes por unidade acadêmica" />
                </EngagementPanel>
                <EngagementPanel title="Cursos com maior volume" subtitle="Até oito cursos; grupos restantes são consolidados" help="Cada barra conta estudantes do curso. O percentual usa o total do recorte; após os oito maiores, os demais cursos são consolidados para manter a leitura simples e proteger grupos pequenos.">
                  <RankedBarChart items={summary.byCourse} ariaLabel="Estudantes por curso" />
                </EngagementPanel>
                <EngagementPanel title="Polos com maior volume" subtitle="Até oito polos; grupos pequenos não aparecem isolados" help="Cada barra conta estudantes do polo. O percentual usa o total do recorte; polos com menos de cinco estudantes não são mostrados isoladamente.">
                  <RankedBarChart items={summary.byPolo} ariaLabel="Estudantes por polo" />
                </EngagementPanel>
                <EngagementPanel title="Nível de engajamento" subtitle="Faixas calculadas sobre o rating de 0 a 3 do snapshot" help="O rating do snapshot é agrupado em sem atividade, baixo, intermediário e alto. Rating ausente ou igual a zero entra em Sem atividade; o percentual usa todos os estudantes do recorte.">
                  <RankedBarChart items={summary.engagement} ariaLabel="Estudantes por nível de engajamento" colors={["#94a3b8", "#d99a1b", "#39769f", "#0f8a69"]} />
                </EngagementPanel>
                <EngagementPanel title="Recência do último acesso" subtitle="Dias entre o último login e a geração do snapshot" help="Calcula os dias entre o último login e a data de geração do snapshot. Sem data válida entra em Sem acesso; o percentual usa todos os estudantes do recorte.">
                  <RankedBarChart items={summary.recency} ariaLabel="Estudantes por recência do último acesso" colors={["#0f8a69", "#39769f", "#d99a1b", "#c45f16", "#64748b"]} />
                </EngagementPanel>
              </section>
            )}
            <p className="engagement-privacy-note engagement-privacy-note--page">Painel exclusivamente agregado: nomes e identificadores individuais não são exibidos. Rankings consolidam grupos de baixa frequência.</p>
          </>
        ) : null}
      </div>
    </PermissionGate>
  );
}

function RankedBarChart({ items, ariaLabel, colors = BAR_COLORS }: {
  items: BreakdownItem[];
  ariaLabel: string;
  colors?: string[];
}) {
  const visibleItems = items.filter((item) => item.value > 0);
  const valueLabels = visibleItems.map((item) => `${formatNumber(item.value)} · ${formatPercent(item.share)}`);
  const options = useMemo<ChartOptions<"bar">>(() => ({
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 450 },
    layout: { padding: { right: 12 } },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#102a43",
        padding: 11,
        displayColors: false,
        callbacks: {
          title: (contexts) => visibleItems[contexts[0]?.dataIndex]?.label || "",
          label: (context) => ` ${valueLabels[context.dataIndex] || ""}`
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        border: { display: false },
        grid: { display: false },
        ticks: { display: false }
      },
      y: {
        border: { display: false },
        grid: { display: false },
        ticks: { color: "#334155", font: { size: 11, weight: 600 }, autoSkip: false }
      }
    }
  }), [valueLabels, visibleItems]);

  if (!visibleItems.length) return <EngagementEmpty />;
  return (
    <div className="engagement-bar-chart" role="img" aria-label={ariaLabel}>
      <Bar
        data={{
          labels: visibleItems.map((item) => shortenLabel(item.label)),
          datasets: [withValueLabels({
            label: "Estudantes",
            data: visibleItems.map((item) => item.value),
            backgroundColor: visibleItems.map((_, index) => colors[index % colors.length]),
            borderRadius: 6,
            borderSkipped: false,
            barPercentage: 0.78,
            categoryPercentage: 0.82
          }, valueLabels)]
        }}
        options={options}
      />
    </div>
  );
}

function shortenLabel(value: string): string {
  return value.length > 26 ? `${value.slice(0, 24)}…` : value;
}

function emptyOptions() {
  return { periodo: [], polo: [], curso: [], ua: [] };
}
